import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ArticleCard from "@/components/ArticleCard";
import api from "@/lib/api";

export default function AuthorPage() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/authors/${slug}`).then(async (authRes) => {
      setAuthor(authRes.data);
      const artRes = await api.get("/articles", { params: { author: authRes.data.id, limit: 20 } });
      setArticles(artRes.data.articles);
    }).catch(console.error).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 md:px-12 py-20"><div className="animate-pulse h-96 bg-muted" /></div>;
  if (!author) return <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center"><h1 className="font-heading text-4xl">Auteur non trouve</h1></div>;

  return (
    <>
      <Helmet><title>{`${author.name} - OFALycée Éditorial`}</title><meta name="description" content={author.bio} /></Helmet>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="author-page">
        <div className="flex flex-col md:flex-row gap-8 items-start mb-16">
          <Avatar className="h-24 w-24">
            <AvatarImage src={author.avatar} alt={author.name} />
            <AvatarFallback className="text-3xl">{author.name?.[0]}</AvatarFallback>
          </Avatar>
          <div>
            <span className="text-sm font-mono tracking-widest uppercase text-primary">{author.role}</span>
            <h1 className="font-heading text-3xl md:text-4xl mt-1" data-testid="author-name">{author.name}</h1>
            <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed whitespace-pre-line">{author.bio}</p>
            {author.social_links?.linkedin && (
              <a href={author.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-sm text-ofa-blue hover:underline" data-testid="author-linkedin">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                LinkedIn
              </a>
            )}
            <p className="text-sm text-muted-foreground mt-2">{author.article_count} article{author.article_count > 1 ? 's' : ''}</p>
          </div>
        </div>
        <h2 className="font-heading text-2xl mb-8">Articles de {author.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
          {articles.map((a) => <ArticleCard key={a.id} article={a} />)}
        </div>
      </div>
    </>
  );
}
